import { SlidersHorizontal, Search } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import { useVoltFilters } from "./voltFilters";

export type VoltFilter = {
  param: string;
  label: string;
  options: { value: string; label: string }[];
  allLabel?: string;
};

// Radix Select rejects an empty item value, so ALL stands for "no filter".
const ALL = "__all";

export const VoltFilterBar = ({
  filters,
  searchParam = "q",
  searchPlaceholder = "Suchen …",
  className,
}: {
  filters: VoltFilter[];
  searchParam?: string | null;
  searchPlaceholder?: string;
  className?: string;
}) => {
  const isMobile = useIsMobile();
  const names = [
    ...(searchParam ? [searchParam] : []),
    ...filters.map((f) => f.param),
  ];
  const { values, activeCount, set, reset } = useVoltFilters(names);
  const selectCount = filters.filter((f) => values[f.param]).length;

  const search = searchParam ? (
    <div className="relative min-w-0 flex-1 sm:max-w-xs">
      <Search
        aria-hidden
        className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        type="search"
        aria-label={searchPlaceholder}
        placeholder={searchPlaceholder}
        value={values[searchParam]}
        onChange={(e) => set(searchParam, e.target.value)}
        className="pl-8"
      />
    </div>
  ) : null;

  const selects = filters.map((f) => (
    <div key={f.param} className="flex flex-col gap-1.5">
      {isMobile ? <Label htmlFor={`vf-${f.param}`}>{f.label}</Label> : null}
      <Select
        value={values[f.param] || ALL}
        onValueChange={(v) => set(f.param, v === ALL ? null : v)}
      >
        <SelectTrigger
          id={`vf-${f.param}`}
          aria-label={f.label}
          className={cn(isMobile ? "w-full" : "w-44")}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>
            {f.allLabel ?? `${f.label}: alle`}
          </SelectItem>
          {f.options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  ));

  const resetButton =
    activeCount > 0 ? (
      <Button variant="ghost" onClick={reset}>
        Filter zurücksetzen
      </Button>
    ) : null;

  if (isMobile) {
    return (
      <div className={cn("mb-4 flex items-center gap-2", className)}>
        {search}
        {filters.length > 0 ? (
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" aria-label="Filter">
                <SlidersHorizontal aria-hidden />
                Filter
                {selectCount > 0 ? (
                  <Badge className="ml-0.5 px-1.5">{selectCount}</Badge>
                ) : null}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="px-4 pb-6">
              <SheetHeader className="px-0">
                <SheetTitle>Filter</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-4">{selects}</div>
              {resetButton}
            </SheetContent>
          </Sheet>
        ) : null}
      </div>
    );
  }

  return (
    <div className={cn("mb-4 flex flex-wrap items-center gap-2", className)}>
      {search}
      {selects}
      {resetButton}
    </div>
  );
};
